import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { authAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'react-hot-toast';
import useDocumentTitle from '../../hooks/useDocumentTitle';
import { countries } from '../../data/countries';
import './AuthPages.css';

interface InvitationData {
  email: string;
  organisation_name: string;
  organisation_id: number;
  role: string;
  inviter_name: string;
  expires_at: string;
  user_exists: boolean;
  user: {
    id: number;
    name: string;
    email: string;
  } | null;
}

const InvitationAcceptPage: React.FC = () => {
  useDocumentTitle('Accept Invitation');
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { user, login, isAuthenticated } = useAuth();

  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [invitation, setInvitation] = useState<InvitationData | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Signup form state (for new users)
  const [name, setName] = useState('');
  const [countryCode, setCountryCode] = useState('+973');
  const [mobile, setMobile] = useState('');

  useEffect(() => {
    fetchInvitation();
  }, [token]);

  const fetchInvitation = async () => {
    if (!token) {
      setError('Invalid invitation link');
      setLoading(false);
      return;
    }

    try {
      const response = await authAPI.getInvitation(token);
      if (response.data.status === 'success') {
        setInvitation(response.data.data);
      } else {
        setError(response.data.message || 'Invalid invitation');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load invitation details');
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptDirect = async () => {
    if (!token) return;

    setAccepting(true);
    try {
      const response = await authAPI.acceptInvitationDirect(token);
      if (response.data.status === 'success') {
        const { token: authToken, user: userData } = response.data;
        login(userData, authToken);
        toast.success(response.data.message || 'You have joined the organization!');
        navigate('/analytics/summary');
      } else {
        toast.error(response.data.message || 'Failed to accept invitation');
      }
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to accept invitation');
    } finally {
      setAccepting(false);
    }
  };

  const handleSignupAndAccept = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token || !name.trim()) {
      toast.error('Please enter your name');
      return;
    }

    setAccepting(true);
    try {
      const response = await authAPI.acceptInvitationAndSignup(token, {
        name,
        country_code: countryCode,
        mobile,
      });

      if (response.data.status === 'success') {
        const { token: authToken, user: userData } = response.data;
        login(userData, authToken);
        toast.success(response.data.message || 'Welcome! You have joined the organization.');
        navigate('/analytics/summary');
      } else {
        toast.error(response.data.message || 'Failed to complete signup');
      }
    } catch (err: any) {
      const errData = err.response?.data;
      // If account already exists, auto-accept directly without needing login
      if (err.response?.status === 409 && errData?.user_exists) {
        try {
          const directRes = await authAPI.acceptInvitationDirect(token);
          if (directRes.data.status === 'success') {
            const { token: authToken, user: userData } = directRes.data;
            login(userData, authToken);
            toast.success(directRes.data.message || 'You have joined the organization!');
            navigate('/analytics/summary');
            return;
          }
        } catch {
          // fall through to generic error
        }
      }
      toast.error(errData?.message || 'Failed to complete signup');
    } finally {
      setAccepting(false);
    }
  };

  if (loading) {
    return (
      <div className="auth-page">
        <div className="auth-container">
          <div className="auth-form-section">
            <div className="auth-form-wrapper text-center">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-3">Loading invitation details...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !invitation) {
    return (
      <div className="auth-page">
        <div className="auth-container">
          <div className="auth-form-section">
            <div className="auth-form-wrapper">
              <Link to="/" className="auth-logo">
                <div className="logo-icon">
                  <span className="logo-text">C</span>
                </div>
                <span className="brand-name">CloudAyn</span>
              </Link>

              <div className="auth-header">
                <h1>Invalid Invitation</h1>
                <p>{error || 'This invitation link is invalid or has expired.'}</p>
              </div>

              <div className="text-center mt-4">
                <Link to="/login" className="btn btn-primary">
                  Go to Login
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Case 1: User is logged in and email matches
  if (isAuthenticated && user?.email?.toLowerCase() === invitation.email.toLowerCase()) {
    return (
      <div className="auth-page">
        <div className="auth-container">
          <div className="auth-form-section">
            <div className="auth-form-wrapper">
              <Link to="/" className="auth-logo">
                <div className="logo-icon">
                  <span className="logo-text">C</span>
                </div>
                <span className="brand-name">CloudAyn</span>
              </Link>

              <div className="auth-header">
                <h1>Join {invitation.organisation_name}</h1>
                <p>
                  {invitation.inviter_name} has invited you to join as <strong>{invitation.role}</strong>
                </p>
              </div>

              <div className="invitation-details">
                <div className="invitation-card">
                  <div className="invitation-org">
                    <i className="bi bi-building"></i>
                    <span>{invitation.organisation_name}</span>
                  </div>
                  <div className="invitation-role">
                    <i className="bi bi-person-badge"></i>
                    <span>Role: {invitation.role}</span>
                  </div>
                  <div className="invitation-inviter">
                    <i className="bi bi-person"></i>
                    <span>Invited by: {invitation.inviter_name}</span>
                  </div>
                </div>
              </div>

              <button
                onClick={handleAcceptDirect}
                className="btn btn-primary btn-lg w-100 mt-4"
                disabled={accepting}
              >
                {accepting ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2"></span>
                    Joining...
                  </>
                ) : (
                  <>
                    Accept & Join Organization
                    <i className="bi bi-check-circle ms-2"></i>
                  </>
                )}
              </button>

              <div className="auth-footer mt-4">
                <Link to="/analytics/summary" className="btn btn-link">
                  Maybe later
                </Link>
              </div>
            </div>
          </div>

          <div className="auth-visual-section">
            <div className="auth-visual-content">
              <div className="visual-shapes">
                <div className="visual-shape shape-1"></div>
                <div className="visual-shape shape-2"></div>
                <div className="visual-shape shape-3"></div>
              </div>

              <div className="visual-text">
                <h2>You're Invited!</h2>
                <p>Join {invitation.organisation_name} and start collaborating with your team.</p>

                <div className="feature-list">
                  <div className="feature-item">
                    <i className="bi bi-check-circle-fill"></i>
                    <span>Access shared assets</span>
                  </div>
                  <div className="feature-item">
                    <i className="bi bi-check-circle-fill"></i>
                    <span>View analytics & reports</span>
                  </div>
                  <div className="feature-item">
                    <i className="bi bi-check-circle-fill"></i>
                    <span>Collaborate with team</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Case 2: User is logged in but email doesn't match
  if (isAuthenticated && user?.email?.toLowerCase() !== invitation.email.toLowerCase()) {
    return (
      <div className="auth-page">
        <div className="auth-container">
          <div className="auth-form-section">
            <div className="auth-form-wrapper">
              <Link to="/" className="auth-logo">
                <div className="logo-icon">
                  <span className="logo-text">C</span>
                </div>
                <span className="brand-name">CloudAyn</span>
              </Link>

              <div className="auth-header">
                <h1>Email Mismatch</h1>
                <p>
                  This invitation was sent to <strong>{invitation.email}</strong>, but you're logged in as <strong>{user?.email}</strong>.
                </p>
              </div>

              <div className="alert alert-warning">
                <i className="bi bi-exclamation-triangle me-2"></i>
                Please log out and sign in with the correct email address to accept this invitation.
              </div>

              <Link
                to={`/login?redirect=/invitation/${token}&email=${encodeURIComponent(invitation.email)}`}
                className="btn btn-primary btn-lg w-100 mt-4"
              >
                Log in with correct account
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Case 3: User exists but not logged in
  if (invitation.user_exists) {
    return (
      <div className="auth-page">
        <div className="auth-container">
          <div className="auth-form-section">
            <div className="auth-form-wrapper">
              <Link to="/" className="auth-logo">
                <div className="logo-icon">
                  <span className="logo-text">C</span>
                </div>
                <span className="brand-name">CloudAyn</span>
              </Link>

              <div className="auth-header">
                <h1>Join {invitation.organisation_name}</h1>
                <p>
                  {invitation.inviter_name} has invited you to join as <strong>{invitation.role}</strong>
                </p>
              </div>

              <div className="invitation-details">
                <div className="invitation-card">
                  <div className="invitation-org">
                    <i className="bi bi-building"></i>
                    <span>{invitation.organisation_name}</span>
                  </div>
                  <div className="invitation-email">
                    <i className="bi bi-envelope"></i>
                    <span>{invitation.email}</span>
                  </div>
                </div>
              </div>

              <button
                onClick={handleAcceptDirect}
                className="btn btn-primary btn-lg w-100 mt-4"
                disabled={accepting}
              >
                {accepting ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2"></span>
                    Joining...
                  </>
                ) : (
                  <>
                    Accept & Join Organization
                    <i className="bi bi-check-circle ms-2"></i>
                  </>
                )}
              </button>

              <div className="auth-footer mt-3 text-center">
                <small className="text-muted">
                  Wrong account?{' '}
                  <Link to={`/login?redirect=/invitation/${token}&email=${encodeURIComponent(invitation.email)}`}>
                    Sign in with a different account
                  </Link>
                </small>
              </div>
            </div>
          </div>

          <div className="auth-visual-section">
            <div className="auth-visual-content">
              <div className="visual-shapes">
                <div className="visual-shape shape-1"></div>
                <div className="visual-shape shape-2"></div>
                <div className="visual-shape shape-3"></div>
              </div>

              <div className="visual-text">
                <h2>Welcome Back!</h2>
                <p>Log in to accept the invitation and join {invitation.organisation_name}.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Case 4: New user - needs to signup
  return (
    <div className="auth-page">
      <div className="auth-container">
        <div className="auth-form-section">
          <div className="auth-form-wrapper">
            <Link to="/" className="auth-logo">
              <div className="logo-icon">
                <span className="logo-text">C</span>
              </div>
              <span className="brand-name">CloudAyn</span>
            </Link>

            <div className="auth-header">
              <h1>Join {invitation.organisation_name}</h1>
              <p>
                {invitation.inviter_name} has invited you to join as <strong>{invitation.role}</strong>.
                Complete your profile to get started.
              </p>
            </div>

            <div className="invitation-details mb-4">
              <div className="invitation-card">
                <div className="invitation-email">
                  <i className="bi bi-envelope-check"></i>
                  <span>{invitation.email}</span>
                </div>
              </div>
            </div>

            <form onSubmit={handleSignupAndAccept} className="auth-form">
              <div className="form-group">
                <label>Full Name</label>
                <div className="input-with-icon">
                  <i className="bi bi-person"></i>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="John Doe"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Phone Number (Optional)</label>
                <div className="row g-2">
                  <div className="col-4">
                    <select
                      className="form-control"
                      value={countryCode}
                      onChange={(e) => setCountryCode(e.target.value)}
                    >
                      <optgroup label="GCC Countries">
                        <option value="+973">{countries.find(c => c.code === 'BH')?.flag} +973 Bahrain</option>
                        <option value="+971">{countries.find(c => c.code === 'AE')?.flag} +971 UAE</option>
                        <option value="+966">{countries.find(c => c.code === 'SA')?.flag} +966 Saudi Arabia</option>
                        <option value="+965">{countries.find(c => c.code === 'KW')?.flag} +965 Kuwait</option>
                        <option value="+968">{countries.find(c => c.code === 'OM')?.flag} +968 Oman</option>
                        <option value="+974">{countries.find(c => c.code === 'QA')?.flag} +974 Qatar</option>
                      </optgroup>
                      <optgroup label="All Countries">
                        {countries.map(country => (
                          <option key={country.code} value={country.phoneCode}>
                            {country.flag} {country.phoneCode} {country.name}
                          </option>
                        ))}
                      </optgroup>
                    </select>
                  </div>
                  <div className="col-8">
                    <div className="input-with-icon">
                      <i className="bi bi-telephone"></i>
                      <input
                        type="tel"
                        className="form-control"
                        placeholder="Mobile number"
                        value={mobile}
                        onChange={(e) => setMobile(e.target.value.replace(/\D/g, ''))}
                        pattern="[0-9]*"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <button type="submit" className="btn btn-primary btn-lg w-100" disabled={accepting}>
                {accepting ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2"></span>
                    Creating Account...
                  </>
                ) : (
                  <>
                    Create Account & Join
                    <i className="bi bi-check-circle ms-2"></i>
                  </>
                )}
              </button>
            </form>

            <div className="auth-divider">
              <span>or</span>
            </div>

            <div className="auth-footer">
              <p>Already have an account?</p>
              <Link to={`/login?redirect=/invitation/${token}&email=${encodeURIComponent(invitation.email)}`} className="auth-link">
                Sign in instead
                <i className="bi bi-arrow-right ms-2"></i>
              </Link>
            </div>
          </div>
        </div>

        <div className="auth-visual-section">
          <div className="auth-visual-content">
            <div className="visual-shapes">
              <div className="visual-shape shape-1"></div>
              <div className="visual-shape shape-2"></div>
              <div className="visual-shape shape-3"></div>
            </div>

            <div className="visual-text">
              <h2>You're Invited!</h2>
              <p>Create your CloudAyn account and join {invitation.organisation_name} in just a few seconds.</p>

              <div className="feature-list">
                <div className="feature-item">
                  <i className="bi bi-check-circle-fill"></i>
                  <span>No password required</span>
                </div>
                <div className="feature-item">
                  <i className="bi bi-check-circle-fill"></i>
                  <span>Instant access to organization</span>
                </div>
                <div className="feature-item">
                  <i className="bi bi-check-circle-fill"></i>
                  <span>Start collaborating immediately</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvitationAcceptPage;
